import { useState } from "react";
import NumberInput from "./NumberInput";

/**
 * Minimal table layout + utilities shared across admin CRUD pages.
 *
 * Usage:
 *   <DataTable
 *     columns={[{ key: 'name', label: 'Tên' }, ...]}
 *     rows={[{ id: '1', name: 'A' }]}
 *     actions={(row) => <ActionIcons row={row} />}
 *   />
 */
export function DataTable({ columns, rows, actions, emptyText = "Chưa có dữ liệu.", testid, onRowClick }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-[#0D1420] border border-white/5 rounded-xl p-12 text-center text-white/40" data-testid={testid ? `${testid}-empty` : undefined}>
        {emptyText}
      </div>
    );
  }
  return (
    <div className="bg-[#0D1420] border border-white/5 rounded-xl overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white/[0.02] border-b border-white/5">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-4 py-3 text-left text-[11px] tracking-[0.18em] uppercase font-mono text-white/50 ${c.className || ""}`}
                >
                  {c.label}
                </th>
              ))}
              {actions && <th className="px-4 py-3 w-[1%] text-right text-[11px] uppercase font-mono text-white/50">Thao tác</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row.id || idx}
                className={`border-b border-white/5 last:border-b-0 hover:bg-white/[0.015] transition ${onRowClick ? "cursor-pointer" : ""}`}
                data-testid={testid ? `${testid}-row-${row.id || idx}` : undefined}
                onClick={onRowClick ? (e) => {
                  // Phase 85.A.80 — Row click opens detail; bỏ qua khi
                  // user click vào input/button/link/icon trong cell.
                  const tag = (e.target.tagName || "").toLowerCase();
                  if (["input", "button", "a", "svg", "path"].includes(tag)) return;
                  if (e.target.closest && e.target.closest("button,a,input,select,textarea")) return;
                  onRowClick(row);
                } : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 ${c.className || ""}`}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
                {actions && <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>{actions(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer, size = "md", testId }) {
  if (!open) return null;
  const w = size === "lg" ? "max-w-2xl" : size === "xl" ? "max-w-5xl" : "max-w-md";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid={testId || "modal"}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${w} bg-[#0D1420] border border-white/10 rounded-2xl shadow-2xl overflow-hidden`}>
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-white/5 text-white/60 hover:text-white flex items-center justify-center"
            data-testid="modal-close"
          >
            ✕
          </button>
        </div>
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">{children}</div>
        {footer && <div className="px-6 py-3 border-t border-white/10 flex items-center justify-end gap-2 bg-white/[0.02]">{footer}</div>}
      </div>
    </div>
  );
}

export function Input({ label, hint, type, value, onChange, min, max, decimal, thousand, ...rest }) {
  // Phase 84.M.45 — When `type="number"`, route through NumberInput (which
  // fixes leading-zero deletion + adds vi-VN thousand-separators). The
  // onChange contract is preserved by wrapping the emitted number back into
  // a fake `event.target.value` shape so legacy parents reading
  // `e.target.value` keep working without changes.
  const isNumber = type === "number";
  const numericValue = isNumber
    ? (value === "" || value === null || value === undefined ? null : Number(value))
    : value;
  return (
    <label className="block mb-3">
      {label && (
        <div className="text-[11px] tracking-[0.2em] uppercase font-mono text-white/60 mb-1.5">{label}</div>
      )}
      {isNumber ? (
        <NumberInput
          value={Number.isFinite(numericValue) ? numericValue : null}
          decimal={decimal === true}
          thousand={thousand !== false}
          min={typeof min === "string" ? Number(min) : min}
          max={typeof max === "string" ? Number(max) : max}
          onChange={(v) => {
            if (typeof onChange !== "function") return;
            onChange({ target: { value: v == null ? "" : String(v) } });
          }}
          className="w-full bg-[#0A0C10] border border-white/15 focus:border-[#3B82F6] outline-none px-3 py-2.5 rounded-lg text-sm transition"
          dataTestid={rest["data-testid"]}
          placeholder={rest.placeholder}
          disabled={rest.disabled}
          autoFocus={rest.autoFocus}
          name={rest.name}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          className="w-full bg-[#0A0C10] border border-white/15 focus:border-[#3B82F6] outline-none px-3 py-2.5 rounded-lg text-sm transition"
          {...rest}
        />
      )}
      {hint && <div className="mt-1 text-[11px] text-white/40">{hint}</div>}
    </label>
  );
}

export function Textarea({ label, hint, ...rest }) {
  return (
    <label className="block mb-3">
      {label && (
        <div className="text-[11px] tracking-[0.2em] uppercase font-mono text-white/60 mb-1.5">{label}</div>
      )}
      <textarea
        rows={3}
        className="w-full bg-[#0A0C10] border border-white/15 focus:border-[#3B82F6] outline-none px-3 py-2.5 rounded-lg text-sm transition resize-y"
        {...rest}
      />
      {hint && <div className="mt-1 text-[11px] text-white/40">{hint}</div>}
    </label>
  );
}

export function Select({ label, options = [], children, className = "", ...rest }) {
  // Phase 81h-fix: hỗ trợ CẢ `options` prop VÀ JSX children. Trước đây
  // chỉ render `options`, làm cho usage `<Select>...children</Select>` ra
  // dropdown rỗng hoàn toàn (bug LiveScreenPage filter + rebind modal).
  return (
    <label className="block mb-3">
      {label && (
        <div className="text-[11px] tracking-[0.2em] uppercase font-mono text-white/60 mb-1.5">{label}</div>
      )}
      <select
        className={`w-full bg-[#0A0C10] border border-white/15 focus:border-[#3B82F6] outline-none px-3 py-2.5 rounded-lg text-sm transition ${className}`}
        {...rest}
      >
        {children
          ? children
          : options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
      </select>
    </label>
  );
}

export function Badge({ color = "green", children }) {
  const palettes = {
    green: "bg-[#3B82F6]/15 text-[#3B82F6]",
    blue: "bg-[#3B82F6]/15 text-[#3B82F6]",
    orange: "bg-[#F59E0B]/15 text-[#F59E0B]",
    red: "bg-[#EF4444]/15 text-[#EF4444]",
    purple: "bg-[#AF52DE]/15 text-[#AF52DE]",
    gray: "bg-white/10 text-white/60",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium font-semibold ${palettes[color]}`}>
      {children}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">{title}</h1>
        {subtitle && <p className="text-white/50 text-sm mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function IconButton({ icon: Icon, onClick, variant = "neutral", title, ...rest }) {
  const styles = {
    neutral: "text-white/60 hover:text-white hover:bg-white/5",
    primary: "text-[#3B82F6] hover:text-[#4ADE80] hover:bg-[#3B82F6]/10",
    danger: "text-red-400 hover:text-red-300 hover:bg-red-500/10",
    warn: "text-amber-300 hover:text-amber-200 hover:bg-amber-500/10",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${styles[variant]}`}
      {...rest}
    >
      <Icon size={16} weight="bold" />
    </button>
  );
}

/** Simple confirm dialog using Modal. */
export function ConfirmDialog({ open, title, message, confirmText = "Xác nhận", danger = false, onConfirm, onCancel, testid }) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm text-white/70 hover:bg-white/5 transition"
            data-testid={testid ? `${testid}-cancel` : undefined}
          >
            Huỷ
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              try {
                setBusy(true);
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              danger
                ? "bg-red-500 hover:bg-red-600 text-white"
                : "bg-gradient-to-r from-[#4F8DFF] to-[#2563EB] hover:from-[#60A5FA] hover:to-[#3B82F6] text-white"
            } disabled:opacity-60`}
            data-testid={testid ? `${testid}-confirm` : undefined}
          >
            {busy ? "Đang xử lý..." : confirmText}
          </button>
        </>
      }
    >
      <p className="text-sm text-white/70 leading-relaxed">{message}</p>
    </Modal>
  );
}
